import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import type { SetRequired } from 'type-fest';

import Locator from '../../../../shared/Locator';
import type { UserProject } from '../../../../shared/organizationPackages';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { nuc } from '../../../utils';
import FallbackImage from '../../generic/FallbackImage';
import FromNow from '../../generic/FromNow';
import { icons } from '../../generic/Icon';
import List from '../../generic/List';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import useFetchPackageVersionProjects, { useRefetchVersionProjectsDetailsOnTeamSelection } from '../hooks/useFetchPackageVersionProjects';
import type { PackageInventoryParams } from '../PackageInventoryContainer';
import { Badge, BadgeColors } from '../../generic/Badge';

type Props = SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;

/**
 * Get the project url base on the given project.
 * If the project doesn't have a branchRevisionLocators list, return a default project url without branch and revision (ensure backward compatibility).
 * Otherwise, return a project url with the branch and revision in the first item of branchRevisionLocators.
 */
function getProjectUrl(project: UserProject): string {
  // We are only providing one link per project, so it doesn't matter which branch we choose.
  const branchRevisionLocators = _.head(project.branchRevisionLocators);
  if (!branchRevisionLocators || !branchRevisionLocators.branch) {
    // Ensure backward compatibility when we don't have the branch data.
    return `/projects/${nuc(project.projectId)}`;
  }
  const { branch, userProjectRevisionLocator } = branchRevisionLocators;
  const locator = new Locator(userProjectRevisionLocator);

  return `/projects/${nuc(locator.toPackageString())}/refs/branch/${nuc(branch)}/${locator.revision.value}`;
}

const PackageProjectsTableBody: VoidFunctionComponent = () => {
  const projects = useSelector(PackageInventoryStore.selectors.getPackageProjects);
  const { errors, isLoading } = useSelector(PackageInventoryStore.selectors.getAsyncStatusGetPackageProjects);
  const hasFilterAndNoPackageProjects = useSelector(PackageInventoryStore.selectors.getHasFilterAndNoPackageProjects);

  if (!_.isEmpty(errors)) {
    return (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  if (isLoading || !projects) {
    return _.times(3, () => <List.RowLoading />);
  }

  if (hasFilterAndNoPackageProjects) {
    return (
      <FallbackImage src="no-results" alt="No projects found" hasPadding>
        No projects found.
      </FallbackImage>
    );
  }

  return projects.map((project) => (
    <List.RowContainer>
      <List.Row
        key={project.projectId}
        to={getProjectUrl(project)}
        testId="package-inventory-package-table-row--projects"
    >
        <List.RowTitle>{project.projectName}</List.RowTitle>
        <List.RowDetails>
          <List.RowDetailsIconItem icon={icons.tags}>
            {project.labels.length > 0 ? project.labels.join(', ') : 'No labels'}
          </List.RowDetailsIconItem>
          <List.RowDetailsIconItem
            icon={icons.userGroup}
            count={project.teams.length > 0 ? project.teams.length : undefined}
        >
            {project.teams.length > 0 ? 'Teams' : 'No teams'}
          </List.RowDetailsIconItem>
          <List.RowDetailsItem>
            Last policy scanned <FromNow date={project.lastAnalyzed} />
          </List.RowDetailsItem>
          {
          !!project.branchRevisionLocators[0]?.branch && (
            <List.RowDetailsIconItem icon={icons.codeBranch}>
              {project.branchRevisionLocators[0]?.branch}
            </List.RowDetailsIconItem>
          )
        }
          {project.isBlocked && (
          <List.RowDetailsIconItem>
            <Badge color={BadgeColors.LightRed}>
              BLOCKED
            </Badge>
          </List.RowDetailsIconItem>
          )}
        </List.RowDetails>
      </List.Row>
    </List.RowContainer>
  ));
};

/**
 * PackageProjectsTable displays the projects which use
 * a specific revision of a package.
 */
const PackageProjectsTable: VoidFunctionComponent<Props> = ({ packageLocator, version }) => {
  const page = useSelector(PackageInventoryStore.selectors.getVersionDetailsPage);
  const count = useSelector(PackageInventoryStore.selectors.getVersionDetailsCount);
  const totalItems = useSelector(PackageInventoryStore.selectors.getTotalPackageProjectsCount);
  const setPage = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setVersionDetailsPage,
    packageLocator,
    version,
  );
  const setCount = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setVersionDetailsCount,
    packageLocator,
    version,
  );

  //  Watch for changes in the team selection, and re-fetch the projects when it changes.
  useRefetchVersionProjectsDetailsOnTeamSelection({ packageLocator, version });

  return (
    <>
      <List>
        <List.Header>
          <List.HeaderText>Projects</List.HeaderText>
        </List.Header>
        <List.Body>
          <PackageProjectsTableBody />
        </List.Body>
      </List>
      <PaginationFooter page={page} count={count} totalItems={totalItems} setPage={setPage} setCount={setCount} />
    </>
  );
};

export default PackageProjectsTable;
